/**
 * The guide's intro video as a managed wikitext token. Three forms, one
 * video, picked through the Media Manager:
 *
 *   [youtubevideo|<id>]        YouTube, the guide IS the video — steps play
 *                              offsets of it ("Video guide")
 *   [video|<youtube url>]      YouTube, plays at the top of the guide
 *                              ("Introduction video"), managed only when it
 *                              leads the intro
 *   [video|<videoid>]          a video uploaded to this platform, plays at
 *                              the top; managed only when it leads the intro
 *
 * The Video field in the Intro tab owns these tokens: the editor is seeded
 * with the intro text stripped of the managed token (GuideEditLib does the
 * server-side strip so the WYSIWYG doc matches), and the save recomposes
 * `token + editor contents`. Mid-text [video|...] embeds, captioned tokens,
 * and non-YouTube embed services are deliberately unmanaged — they stay
 * inline content, invisible to this field.
 */

export type IntroVideo =
   | {
        type: 'youtube';
        videoId: string;
        /**
         * 'guide': steps play offsets of the video. 'intro': plays at the
         * top. Only YouTube videos offer the choice.
         */
        mode: 'intro' | 'guide';
     }
   | {
        /** A video uploaded to this platform; always plays at the top. */
        type: 'native';
        videoid: number;
     };

// Keep these in sync with GuideEditLib::splitIntroVideo(), which applies the
// same rules server-side to seed the editor with stripped text.
const YOUTUBEVIDEO_TOKEN = /\[youtubevideo\|\s*([^\]|]+?)\s*\][^\S\n]*\n{0,2}/;
const LEADING_VIDEO_TOKEN = /^\s*\[video\|([^\]|]+)(?:\|[^\]]*)?\][^\S\n]*\n{0,2}/;

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;

/** The YouTube video id from a URL, a share link, or a bare 11-char id. */
export function parseYouTubeId(input: string): string | null {
   const text = input.trim();
   if (YOUTUBE_ID.test(text)) {
      return text;
   }
   if (!/^https?:\/\/([\w-]+\.)?(youtube\.com|youtu\.be)\//.test(text)) {
      return null;
   }
   const match =
      text.match(/[?&]v=([A-Za-z0-9_-]{11})/) ??
      text.match(/youtu\.be\/([A-Za-z0-9_-]{11})/) ??
      text.match(/\/(?:shorts|embed|live)\/([A-Za-z0-9_-]{11})/);
   return match ? match[1] : null;
}

export function watchUrl(videoId: string): string {
   return `https://www.youtube.com/watch?v=${videoId}`;
}

/**
 * The leading [video|arg] token match, but only when this field manages it:
 * a hosted videoid or a YouTube URL, with no caption body (stripping a
 * captioned token would orphan its `caption[/video]` tail).
 */
function managedLeadingVideo(text: string): { match: string; video: IntroVideo } | null {
   const match = text.match(LEADING_VIDEO_TOKEN);
   if (!match) {
      return null;
   }
   if (/^[^\n]*\[\/video\]/.test(text.slice(match[0].length))) {
      return null;
   }
   const arg = match[1].trim();
   if (/^\d+$/.test(arg)) {
      return { match: match[0], video: { type: 'native', videoid: Number(arg) } };
   }
   const videoId = parseYouTubeId(arg);
   if (videoId !== null) {
      return { match: match[0], video: { type: 'youtube', videoId, mode: 'intro' } };
   }
   return null;
}

/** The managed video in the given intro wikitext, or null. */
export function parseIntroVideo(text: string): IntroVideo | null {
   const youtubevideo = text.match(YOUTUBEVIDEO_TOKEN);
   if (youtubevideo) {
      return { type: 'youtube', videoId: youtubevideo[1], mode: 'guide' };
   }
   return managedLeadingVideo(text)?.video ?? null;
}

/** The intro wikitext without its managed video token. */
export function stripIntroVideo(text: string): string {
   const youtubevideo = text.match(YOUTUBEVIDEO_TOKEN);
   if (youtubevideo) {
      return text.replace(youtubevideo[0], '');
   }
   const leading = managedLeadingVideo(text);
   return leading ? text.slice(leading.match.length) : text;
}

function tokenFor(video: IntroVideo): string {
   if (video.type === 'native') {
      return `[video|${video.videoid}]`;
   }
   return video.mode === 'guide'
      ? `[youtubevideo|${video.videoId}]`
      : `[video|${watchUrl(video.videoId)}]`;
}

/**
 * The intro wikitext carrying exactly the given video: any managed token is
 * replaced, and null clears it. Unmanaged tokens pass through untouched.
 */
export function applyIntroVideo(text: string, video: IntroVideo | null): string {
   const stripped = stripIntroVideo(text);
   if (video === null) {
      return stripped;
   }
   const token = tokenFor(video);
   return stripped.trim() === '' ? token : `${token}\n\n${stripped}`;
}
